import type { FullTradeCompany, Incoterms } from 'api';
import {
  DateField,
  MultiCarrierAutocompleteField,
  PortAutocomplete,
  SiteAutocomplete,
  TextField,
  TradeCompanyAutocomplete,
  TradeCompanySiteAutocomplete,
  TradeCompanySiteSelector,
  useFormContext,
  useWatch,
} from 'form';
import { useTranslation } from 'i18n';
import { useContext, useEffect } from 'react';
import { SectionContainer } from 'ui';

import { SalesOrderFormContext } from '../../contexts/SalesOrderFormContext';
import type { SalesOrderCreateFormValues } from '../../pages/SalesOrderCreate/SalesOrderCreate';
import type { SalesOrderUpdateFormValues } from '../../pages/SalesOrderUpdate/SalesOrderUpdate';
import { BillTypeSelectField } from '../BillTypeSelectField/BillTypeSelectField';
import { TransportMethod } from '../TransportMethod/TransportMethod';

export const BuyerLogisticsSection = () => {
  const { t } = useTranslation('orders');
  const { incoterm, end_processor_id: endProcessor, purchaser_id: purchaser } = useWatch<SalesOrderCreateFormValues>();
  const { defaultValues, variant } = useContext(SalesOrderFormContext);
  const { resetField } = useFormContext();
  useEffect(() => {
    resetField('final_destination_site_id');
    resetField('delivery_site_id');
  }, [resetField, endProcessor, purchaser]);

  return (
    <SectionContainer title={t('buyerLogisticsSection.title')}>
      <TradeCompanySiteSelector
        name="final_destination_site_id"
        label={t('buyerLogisticsSection.labels.finalDestinationSite')}
        tradeCompanyId={endProcessor}
        required
        infoTooltip={t('buyerLogisticsSection.tooltips.finalDestinationSite')}
      />
      <DateField
        name="target_delivery_date"
        label={t('buyerLogisticsSection.labels.targetDeliveryDate')}
        required
        infoTooltip={t('buyerLogisticsSection.tooltips.targetDeliveryDate')}
        fullWidth
      />
      <TextField
        name="max_number_of_shipments"
        label={t('buyerLogisticsSection.labels.maxNumberOfShipments')}
        infoTooltip={t('buyerLogisticsSection.tooltips.maxNumberOfShipments')}
      />
      <MultiCarrierAutocompleteField
        name="allowed_shipping_lines_ids"
        label={t('buyerLogisticsSection.labels.allowedShippingLines')}
        infoTooltip={t('buyerLogisticsSection.tooltips.allowedShippingLines')}
      />
      <TransportMethod required />
      <TextField
        type="number"
        name="free_time_required"
        label={t('buyerLogisticsSection.labels.freeTimeRequired')}
        infoTooltip={t('buyerLogisticsSection.tooltips.freeTimeRequired')}
      />
      <IncotermSpecificFields
        incoterm={incoterm}
        purchaser={purchaser}
        defaultValues={defaultValues}
        endProcessor={endProcessor}
      />
      {variant === 'update' ? (
        <>
          <TradeCompanyAutocomplete
            name="shipper_id"
            label={t('buyerLogisticsSection.labels.shipper')}
            defaultValue={defaultValues?.shipper_id}
          />
          <TradeCompanyAutocomplete
            name="notify_party_id"
            label={t('buyerLogisticsSection.labels.notifyParty')}
            defaultValue={defaultValues?.notify_party_id}
          />
          <BillTypeSelectField name="bill_type" label={t('buyerLogisticsSection.labels.billType')} />
          <TextField name="marks_and_numbers" label={t('buyerLogisticsSection.labels.marksAndNumbers')} />
          <TextField
            type="text"
            name="shipping_instruction_notes"
            label={t('buyerLogisticsSection.labels.shippingInstructionNotes')}
            minRows={2}
            multiline
          />
        </>
      ) : null}
    </SectionContainer>
  );
};

interface IncotermSpecificFieldsProps {
  incoterm: Incoterms | undefined;
  purchaser: FullTradeCompany['id'] | undefined;
  endProcessor: FullTradeCompany['id'] | undefined;
  defaultValues?: SalesOrderUpdateFormValues;
}

const IncotermSpecificFields = ({ incoterm, purchaser, endProcessor, defaultValues }: IncotermSpecificFieldsProps) => {
  const { t } = useTranslation('orders');

  switch (incoterm) {
    case 'EXW':
      return (
        <>
          <SiteAutocomplete
            name="collection_site_id"
            label={t('buyerLogisticsSection.labels.collectionSite')}
            required
            defaultValue={defaultValues?.collection_site_id}
            infoTooltip={t('buyerLogisticsSection.tooltips.collectionSite')}
          />
          <PortAutocomplete
            name="port_of_discharge_id"
            label={t('buyerLogisticsSection.labels.portOfDischarge')}
            defaultValue={defaultValues?.port_of_discharge_id}
            infoTooltip={t('buyerLogisticsSection.tooltips.portOfDischarge')}
          />
          <PortAutocomplete
            name="icd_id"
            label={t('buyerLogisticsSection.labels.icd')}
            type="ICD"
            defaultValue={defaultValues?.icd_id}
            infoTooltip={t('buyerLogisticsSection.tooltips.icd')}
          />
        </>
      );
    case 'FOB':
      return (
        <>
          <PortAutocomplete
            name="port_of_loading_id"
            label={t('buyerLogisticsSection.labels.portOfLoading')}
            defaultValue={defaultValues?.port_of_loading_id}
            infoTooltip={t('buyerLogisticsSection.tooltips.portOfLoading')}
            required
          />
          <PortAutocomplete
            name="port_of_discharge_id"
            label={t('buyerLogisticsSection.labels.portOfDischarge')}
            defaultValue={defaultValues?.port_of_discharge_id}
            infoTooltip={t('buyerLogisticsSection.tooltips.portOfDischarge')}
          />
          <PortAutocomplete
            name="icd_id"
            label={t('buyerLogisticsSection.labels.icd')}
            type="ICD"
            defaultValue={defaultValues?.icd_id}
            infoTooltip={t('buyerLogisticsSection.tooltips.icd')}
          />
        </>
      );
    case 'CFR':
    case 'CIF':
    case 'CPT':
    case 'CIP':
      return (
        <>
          <TradeCompanySiteAutocomplete
            name="delivery_site_id"
            label={t('buyerLogisticsSection.labels.deliverySite')}
            tradeCompanies={[purchaser, endProcessor].filter((value) => value !== null && value !== undefined)}
            infoTooltip={t('buyerLogisticsSection.tooltips.deliverySite')}
          />
          <PortAutocomplete
            name="port_of_discharge_id"
            label={t('buyerLogisticsSection.labels.portOfDischarge')}
            defaultValue={defaultValues?.port_of_discharge_id}
            required
            infoTooltip={t('buyerLogisticsSection.tooltips.portOfDischarge')}
          />
          <PortAutocomplete
            name="icd_id"
            label={t('buyerLogisticsSection.labels.icd')}
            type="ICD"
            defaultValue={defaultValues?.icd_id}
            infoTooltip={t('buyerLogisticsSection.tooltips.icd')}
          />
        </>
      );
    case 'DAP':
    case 'DDP':
      return (
        <>
          <TradeCompanySiteAutocomplete
            name="delivery_site_id"
            label={t('buyerLogisticsSection.labels.deliverySite')}
            required
            tradeCompanies={[purchaser, endProcessor].filter((value) => value !== null && value !== undefined)}
            defaultValue={defaultValues?.delivery_site_id ?? undefined}
            infoTooltip={t('buyerLogisticsSection.tooltips.deliverySite')}
          />
          <PortAutocomplete
            name="port_of_discharge_id"
            label={t('buyerLogisticsSection.labels.portOfDischarge')}
            defaultValue={defaultValues?.port_of_discharge_id}
            infoTooltip={t('buyerLogisticsSection.tooltips.portOfDischarge')}
          />
          <PortAutocomplete
            name="icd_id"
            label={t('buyerLogisticsSection.labels.icd')}
            type="ICD"
            defaultValue={defaultValues?.icd_id}
            infoTooltip={t('buyerLogisticsSection.tooltips.icd')}
          />
        </>
      );
    default:
      return null;
  }
};
